import React, { useEffect, useRef, useState } from 'react';
import {
  arrayOf, func, number, oneOfType, shape, string
} from 'prop-types';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { Image } from '@thd-olt-component-react/image';
import { HotspotDetails } from './sub-components/HotspotDetails';
import '../../styles/hotspot.scss';

const Hotspot = ({
  apiKey, hsXPosition, hsYPosition, itemID, product, onClick, storeId, productSkus, channel
}) => {
  const hotspotRef = useRef();
  const iconUrl = 'https://assets.thdstatic.com/images/v1/product-hotspot.svg';
  const closeUrl = 'https://assets.thdstatic.com/images/v1/close-symbol-orange.svg';

  const [imageSrc, setImageSrc] = useState(iconUrl);
  const [skip, setSkip] = useState(true);

  const deselectHotspot = (container, img) => {
    const buttonContainer = container;
    buttonContainer.className = 'tag';
    img.setAttribute('src', iconUrl);
  };

  const deselectPreviousHotspot = (container) => {
    const activeHotspots = [
      ...document.getElementsByClassName('tag tippy-active')
    ];
    const previouslySelected = activeHotspots.find((hotspot) => hotspot !== container);
    if (previouslySelected) {
      deselectHotspot(previouslySelected, previouslySelected.children[0]);
    }
  };

  const selectHotspot = (container, img) => {
    const buttonContainer = container;
    img.setAttribute('src', closeUrl);
    buttonContainer.className = 'tag tippy-active';
    setSkip(false);
  };

  const toggleActiveClass = (event) => {
    const container = event?.currentTarget;
    const img = container?.children[0];
    const notSelected = container?.getAttribute('aria-expanded') === 'false';
    if (img && container) {
      if (notSelected) {
        deselectPreviousHotspot(container);
        selectHotspot(container, img);
      } else {
        const timeToWait = 300;
        // Allow time for the tooltip to close so that deselection of hotspot is synchronized
        setTimeout(() => {
          deselectHotspot(container, img);
        }, timeToWait);
      }
    }
  };

  const hotspotClick = (event) => {
    onClick(event, hotspotRef.current);
    toggleActiveClass(event);

    LIFE_CYCLE_EVENT_BUS.trigger('product-hotspots.tooltip', {
      channel,
      itemID,
      leftPosition: hsXPosition,
      product,
      topPosition: hsYPosition
    });
  };

  const removeActiveClass = (event) => {
    const targetId = event?.currentTarget?.id;
    const parentId = event?.currentTarget?.parentNode?.id;
    const eventClassName = event?.target?.className;
    const carouselArrows = eventClassName === 'carousel__prev' || eventClassName === 'carousel__next';
    const clickedOutsideTag = targetId !== 'hotspot-container' && parentId !== 'hotspot-container'
      && !carouselArrows;
    const active = hotspotRef && hotspotRef.current && hotspotRef.current.className === 'tag tippy-active';
    if (clickedOutsideTag && active) {
      // if the user clicks outside of the tag, we want to close the tooltip
      if ((eventClassName !== 'icon' && eventClassName !== 'tag tippy-active')
        && eventClassName.indexOf('details') === -1) {
        let hotspotRefTemp = null;
        onClick(event, hotspotRefTemp);
      }
      const container = hotspotRef.current;
      const img = hotspotRef.current.children[0];
      const timeToWait = 150;
      // Allow time for the tooltip to close so that deselection of hotspot is synchronized
      setTimeout(() => {
        deselectHotspot(container, img);
      }, timeToWait);
    }
  };

  useEffect(() => {
    document.body.addEventListener('click', removeActiveClass);
    return () => {
      document.body.removeEventListener('click', removeActiveClass);
    };
  }, []);

  useEffect(() => {
    if (hotspotRef?.current?.className === 'tag') {
      setImageSrc(iconUrl);
    }
  }, [hotspotRef?.current?.className]);

  const imageStyle = {
    cursor: 'pointer',
    left: `${hsXPosition * 100}%`,
    top: `${hsYPosition * 100}%`
  };

  return (
    <div id={`tooltip-wrapper-div-${itemID}`}>
      <Tooltip
        boundary="viewport"
        channel="desktop"
        content={(
          <HotspotDetails
            apiKey={apiKey}
            itemID={itemID}
            skip={skip}
            sku={product}
            productSkus={productSkus}
            storeId={storeId}
            channel={channel}
          />
        )}
        closeButton
        showClose={false}
        interactive
        appendTo={() => document.querySelector(`#tooltip-wrapper-div-${itemID}`)}
      >
        <button
          tabIndex="0"
          type="button"
          className="tag"
          data-testid="hotspot-container"
          onClick={hotspotClick}
          ref={hotspotRef}
          style={imageStyle}
        >
          <Image
            tabIndex="-1"
            alt={itemID}
            className="icon"
            data-testid="hotspot-image"
            src={imageSrc}
            title={product?.identifiers?.productLabel}
            width="25"
            height="25"
          />
        </button>
      </Tooltip>
    </div>
  );
};

Hotspot.displayName = 'Hotspot';

Hotspot.propTypes = {
  apiKey: string.isRequired,
  channel: string.isRequired,
  hsXPosition: string.isRequired,
  hsYPosition: string.isRequired,
  itemID: string.isRequired,
  onClick: func.isRequired,
  productSkus: arrayOf(string).isRequired,
  product: shape({
    info: shape({
      brandName: string,
      imageUrl: string,
      productLabel: string
    }),
    productUrl: string,
    ratingsReviews: shape({
      averageRating: oneOfType([number, string]),
      totalReviews: oneOfType([number, string])
    })
  }).isRequired,
  storeId: string.isRequired
};

export { Hotspot };
